import React, { useState, useEffect } from 'react';
import { FlipHorizontal, FlipVertical } from 'lucide-react';
import { TemplateSettings } from '../../types/design';

interface TemplatePropertiesPanelProps {
  settings: TemplateSettings;
  onSettingsChange: (settings: Partial<TemplateSettings>) => void;
  onClose: () => void;
}

export const TemplatePropertiesPanel: React.FC<TemplatePropertiesPanelProps> = ({
  settings,
  onSettingsChange
}) => {
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const updateSetting = (key: keyof TemplateSettings, value: any) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    onSettingsChange({ [key]: value });
  };

  return (
    <div className="flex items-center gap-4 py-1 h-full w-max">
      {/* Page Title */}
      <div className="flex items-center px-3 border-r border-gray-100 h-8">
        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Page Settings</span>
      </div>

      {/* Flip */}
      <div className="flex items-center gap-1 px-3 border-r border-gray-100 h-8">
        <button
          onClick={() => updateSetting('flipHorizontal', !localSettings.flipHorizontal)}
          className={`p-1.5 rounded transition-colors ${
            localSettings.flipHorizontal ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-100'
          }`}
          title="Flip Horizontal"
        >
          <FlipHorizontal className="w-4 h-4" />
        </button>
        <button
          onClick={() => updateSetting('flipVertical', !localSettings.flipVertical)}
          className={`p-1.5 rounded transition-colors ${
            localSettings.flipVertical ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-100'
          }`}
          title="Flip Vertical"
        >
          <FlipVertical className="w-4 h-4" />
        </button>
      </div>

      {/* Opacity */}
      <div className="flex items-center gap-2 px-3 border-r border-gray-100 h-8">
        <span className="text-[10px] text-gray-400 font-bold uppercase">Global Opacity</span>
        <input
          type="range"
          value={localSettings.opacity}
          onChange={(e) => updateSetting('opacity', parseInt(e.target.value))}
          min="0"
          max="100"
          className="w-24 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
        />
        <span className="text-[10px] text-gray-600 font-bold w-6">{localSettings.opacity}%</span>
      </div>

      {/* Duration */}
      <div className="flex items-center gap-2 px-3 h-8">
        <span className="text-[10px] text-gray-400 font-bold uppercase">Duration</span>
        <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded border border-gray-100">
          <span className="text-[11px] font-bold text-gray-700">
            {Math.round((localSettings.duration || 20000) / 1000)}
          </span>
          <span className="text-[10px] text-gray-400 font-bold">s</span>
        </div>
      </div>
    </div>
  );
};

